import {useTemplate} from 'Event/TemplateProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useConfig} from 'organization/Event/Configurable'
import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import React from 'react'
import TextField from 'lib/ui/TextField'
import ContentInput from 'Event/Dashboard/components/NavButton/NavButtonConfig/ContentInput'
import TypographySection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/TypographySection'
import BackgroundSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/BackgroundSection'
import SizeSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/SizeSection'
import BorderSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/BorderSection'
import {useAddTranslation} from 'Event/LanguageProvider'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {useAttendeeVariables} from 'Event'
import ButtonStyleClipboardActions, {
  ButtonStyleClipboard,
} from 'lib/ui/Button/ButtonStyleClipboardActions'

export default function SubmitButtonConfig() {
  const saveTemplate = useSaveTemplate()
  const {
    waiver: {button, submittingText},
  } = useTemplate()
  const {
    form: {register, control, watch, setValue},
  } = useConfig()

  const addTranslation = useAddTranslation()
  const v = useAttendeeVariables()

  const save = (data: any) => {
    const {submittingText, ...buttonData} = data

    const text = replaceAtPath(buttonData, 'text', '{{waiver_submit_button}}')

    saveTemplate({
      waiver: {
        button: buttonData,
        submittingText: '{{waiver_submitting_text}}',
      },
      localization: addTranslation({
        waiver_submit_button: text ?? '',
        waiver_submitting_text: submittingText ?? '',
      }),
    })
  }

  const pasteButtonStyle = (style: ButtonStyleClipboard) => {
    const set = (name: string, value: unknown) => {
      if (value === undefined) {
        return
      }

      setValue(name, value, {shouldDirty: true})
    }

    set('disableHover', style.disableHover)

    // The hover color inputs only mount on the next render when the pasted
    // disableHover reveals them, so the rest is set a tick later.
    setTimeout(() => {
      set('font', style.font)
      set('fontSize', style.fontSize)
      set('textColor', style.textColor)
      set('hoverTextColor', style.hoverTextColor)
      set('backgroundColor', style.backgroundColor)
      set('hoverBackgroundColor', style.hoverBackgroundColor)
      set('borderColor', style.borderColor)
      set('hoverBorderColor', style.hoverBorderColor)
      set('borderWidth', style.borderWidth)
      set('borderRadius', style.borderRadius)
      set('height', style.height)
      set('size', style.widthGrid)
    }, 0)
  }

  return (
    <Config
      title="Submit Button"
      onSave={save}
      preview={{
        waiver: {
          button: watch(),
        },
      }}
    >
      <SettingsPanel>
        <ContentInput
          button={button}
          control={control}
          register={register}
          watch={watch}
        />
        <TextField
          label="Submitting Text"
          name="submittingText"
          defaultValue={v(submittingText)}
          inputProps={{
            ref: register,
            'aria-label': 'submitting text',
          }}
          fullWidth
        />
      </SettingsPanel>
      <StylingPanel>
        <ButtonStyleClipboardActions
          values={{
            font: watch('font', button.font),
            fontSize: watch('fontSize', button.fontSize),
            textColor: watch('textColor', button.textColor),
            hoverTextColor: watch('hoverTextColor', button.hoverTextColor),
            backgroundColor: watch('backgroundColor', button.backgroundColor),
            hoverBackgroundColor: watch(
              'hoverBackgroundColor',
              button.hoverBackgroundColor,
            ),
            disableHover: watch('disableHover', button.disableHover),
            borderColor: watch('borderColor', button.borderColor),
            hoverBorderColor: watch(
              'hoverBorderColor',
              button.hoverBorderColor,
            ),
            borderWidth: watch('borderWidth', button.borderWidth),
            borderRadius: watch('borderRadius', button.borderRadius),
            height: watch('height', button.height),
            widthGrid: watch('size', button.size),
          }}
          onPaste={pasteButtonStyle}
        />
        <TypographySection
          button={button}
          control={control}
          storageKey="waiver.submitButton.config.typography"
        />
        <BackgroundSection
          button={button}
          control={control}
          watch={watch}
          storageKey="waiver.submitButton.config.background"
        />
        <SizeSection
          button={button}
          control={control}
          storageKey="waiver.submitButton.config.size"
          disableNewLine
        />
        <BorderSection
          button={button}
          control={control}
          storageKey="waiver.submitButton.config.border"
        />
      </StylingPanel>
    </Config>
  )
}
